import React from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import { DateTime } from 'luxon'

interface Block {
  id: string
  blockIndex: number
  startTime: string
  endTime: string
  pickedBy: { id: string; user: { id: string; name: string } } | null
  pickedAt: string | null
}

interface Participant {
  id: string
  user: { id: string; name: string }
}

interface ScheduleSelectionBlockGridProps {
  blocks: Block[]
  participants: Participant[]
  timeZone: string
}

const COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#c62828',
  '#00838f',
  '#4e342e',
  '#283593',
]

export default function ScheduleSelectionBlockGrid(
  props: ScheduleSelectionBlockGridProps,
): React.JSX.Element {
  const { blocks, participants, timeZone } = props

  const participantColorMap: Record<string, string> = {}
  participants.forEach((p, i) => {
    participantColorMap[p.id] = COLORS[i % COLORS.length]
  })

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 1,
      }}
    >
      {blocks.map((block) => {
        const start = DateTime.fromISO(block.startTime, { zone: timeZone })
        const end = DateTime.fromISO(block.endTime, { zone: timeZone })
        const picked = block.pickedBy !== null

        return (
          <Card
            key={block.id}
            variant='outlined'
            sx={{
              borderLeft: picked
                ? `4px solid ${participantColorMap[block.pickedBy!.id] || '#999'}`
                : '4px solid #e0e0e0',
              opacity: picked ? 1 : 0.7,
            }}
          >
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Typography variant='caption' color='text.secondary'>
                Block {block.blockIndex + 1}
              </Typography>
              <Typography variant='body2'>
                {start.toFormat('MMM d')} - {end.toFormat('MMM d, yyyy')}
              </Typography>
              {picked ? (
                <Chip
                  size='small'
                  label={block.pickedBy!.user.name}
                  sx={{
                    mt: 0.5,
                    bgcolor: participantColorMap[block.pickedBy!.id] || '#999',
                    color: '#fff',
                  }}
                />
              ) : (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Available
                </Typography>
              )}
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )
}
